import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUpdateUser, useRestoreUser, useEmployeeTypes, useAdminUsers } from '../../hooks/useAdmin';
import { useAuth } from '../../services/authContext';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { Modal, ConfirmModal } from '../../components/common/Modal';
import { DismissibleErrorBanner } from '../../components/common/DismissibleErrorBanner';
import { useAuditTrail } from '../../hooks/useAuditTrail';
import { useUserRoleTags, useSetUserRoleTags } from '../../hooks/useCapacity';
import { useProjectRoles } from '../../hooks/useLeads';
import { StateHistory } from '../../components/workflow/StateHistory';
import { OnboardingPanel } from '../../components/workflow/OnboardingPanel';
import { ArchiveUserModal } from '../../components/admin/ArchiveUserModal';
import {
  useEmploymentRelationships,
  useRecordEmploymentChange,
  useDeleteEmploymentRelationship,
} from '../../hooks/useEmploymentRelationships';
import { useVacationBalance } from '../../hooks/useVacationBalance';
import {
  useEmployeeSalary,
  useEmployeeSalaryHistory,
  useSetEmployeeSalary,
} from '../../hooks/useEmployeeSalary';
import {
  DEDUCTED_FIELD_LABELS,
  type DeductedField,
  type EmployeeSalaryHistoryEntry,
} from '../../services/api/employeeSalary';
import {
  getAdminUser,
  getUserRates,
  createUserRate,
  deleteUserRate,
  getUserAssignments,
  type AdminUser,
  type UserRate,
  type UserAssignment,
  type UpdateUserInput,
  type ClaudeTierChoice,
} from '../../services/api/admin';
import { updateAssignment, deleteAssignment, type UpdateAssignmentInput } from '../../services/api/projects';
import AssignmentForm from '../../components/domain/AssignmentForm';
import { formatCurrency, formatLocalDate, formatDisplayDate } from '../../utils/format';
import { availableRoles, roleAliasMap, normalizeRoles, BACKEND_ROLE_ENUM } from '../../utils/roles';
import { formatDayMonthShort } from '../../utils/dateFormat';
import { DateField } from '../../components/ui/DateField';
// MKS-393 — merch sizes.
import { UserMerchSizesCard } from '../../components/hr/UserMerchSizesCard';

// Quick 260525-k1l: backend userBodySchema requires startDate/endDate to be
// strict YYYY-MM-DD or null. Anything else (ISO datetime, empty string,
// partial input) gets normalized to null to keep the PUT payload schema-valid.
function toYmdOrNull(value: string | null | undefined): string | null {
  if (!value || typeof value !== 'string') return null;
  const prefix = value.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(prefix) ? prefix : null;
}

const SALARY_FIELD_LABELS: Record<string, string> = {
  netAmount: 'Net salary',
  grossAmount: 'Gross salary',
  totalCostAmount: 'Super gross cost',
  deductedField: 'Deducted amount',
};

function formatSalaryValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') return formatCurrency(value);
  return String(value);
}

/**
 * Phase 84 — renders one salary audit entry. The `changes` payload is
 * per-state: `created` carries a flat snapshot, `updated` carries per-field
 * { from, to }, `deleted` carries the prior values under `before`. Switching on
 * state is mandatory, not defensive.
 */
function SalaryHistoryDetail({ entry }: { entry: EmployeeSalaryHistoryEntry }) {
  const { state, changes } = entry;

  if (state === 'updated' && changes.fields) {
    return (
      <ul className="mt-1 space-y-0.5 text-gray-700">
        {Object.entries(changes.fields).map(([field, diff]) => {
          const d = diff as { from?: unknown; to?: unknown };
          return (
            <li key={field}>
              {SALARY_FIELD_LABELS[field] ?? field}: {formatSalaryValue(d.from)} →{' '}
              <span className="font-medium">{formatSalaryValue(d.to)}</span>
            </li>
          );
        })}
      </ul>
    );
  }

  const snapshot = (state === 'deleted' ? changes.before : changes.fields) as
    | Record<string, unknown>
    | undefined;
  if (!snapshot) return null;

  return (
    <ul className="mt-1 space-y-0.5 text-gray-700">
      {Object.entries(snapshot).map(([field, value]) => (
        <li key={field}>
          {SALARY_FIELD_LABELS[field] ?? field}: {formatSalaryValue(value)}
        </li>
      ))}
    </ul>
  );
}

export default function UserDetailPage() {
  // todayStr in useMemo deps ensures recompute on day boundary
  // (same fix applied in F-2203 for stale-date captures).
  const _now = new Date();
  const todayStr = formatLocalDate(_now);

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user: currentUser, startImpersonating, isFinanceOnly, isImpersonating } = useAuth();
  const userId = id ? parseInt(id, 10) : undefined;
  // quick-260813-hob: the backend requireFinanceOnly gate is the
  // authoritative check — this is a UI-level hide so a non-finance admin
  // never sees the figures or triggers a 403. The `!isImpersonating` term
  // matches the Sidebar treatment and fails closed, because the backend
  // resolves roles from the REAL user (D-13) while this context reflects
  // the impersonated one.
  const canSeeSalary = isFinanceOnly && !isImpersonating;
  const { data: auditTrail } = useAuditTrail('User', userId ?? 0);
  const { data: userRoleTagsData } = useUserRoleTags(userId);
  const { data: allProjectRolesData } = useProjectRoles();
  const setUserTagsMutation = useSetUserRoleTags();

  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<UpdateUserInput>({});
  const [editError, setEditError] = useState<string | null>(null);
  // F4-002 — Single page-level banner for create/delete-rate and
  // update/delete-assignment mutation rejections. Without this, a 4xx/5xx
  // response leaves the modal stuck open with no feedback (same defect as
  // F2-005 / F3-003).
  const [actionError, setActionError] = useState<string | null>(null);

  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [skipOnboarding, setSkipOnboarding] = useState(false);
  // FBV-01: Claude tier chosen for this restore. Reset to Standard when the modal opens
  // (mirrors the setSkipOnboarding(false) call on the Restore button below).
  const [restoreTier, setRestoreTier] = useState<ClaudeTierChoice>('claude-standard');
  const [showPastAssignments, setShowPastAssignments] = useState(false);
  const [showInactiveProjects, setShowInactiveProjects] = useState(false);
  const [assignmentSort, setAssignmentSort] = useState<{ key: 'project' | 'startDate' | 'endDate'; dir: 'asc' | 'desc' }>({ key: 'startDate', dir: 'desc' });
  const [showRateHistory, setShowRateHistory] = useState(false);
  const [rateModalOpen, setRateModalOpen] = useState(false);
  const [highlightedRateId, setHighlightedRateId] = useState<number | null>(null);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [editingAssignmentId, setEditingAssignmentId] = useState<number | null>(null);
  const [deleteAssignmentId, setDeleteAssignmentId] = useState<number | null>(null);

  const { data: user, isLoading: loadingUser, error: userError } = useQuery({
    queryKey: ['adminUser', userId],
    queryFn: () => getAdminUser(userId!),
    enabled: !!userId,
  });

  const { data: rates, isLoading: loadingRates } = useQuery({
    queryKey: ['userRates', userId],
    queryFn: () => getUserRates(userId!),
    enabled: !!userId,
  });

  const { data: assignments, isLoading: loadingAssignments } = useQuery({
    queryKey: ['userAssignments', userId, showPastAssignments, showInactiveProjects],
    queryFn: () => getUserAssignments(userId!, showPastAssignments, showInactiveProjects),
    enabled: !!userId,
  });

  const { data: employeeTypes } = useEmployeeTypes();
  const updateMutation = useUpdateUser();
  const restoreMutation = useRestoreUser();

  const employeeTypeMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const type of employeeTypes || []) {
      map.set(type.id, type.name || 'Unknown');
    }
    return map;
  }, [employeeTypes]);

  // Phase 71 (VAC-04, D-02) — category ('employee' | 'contractor' | null) per
  // employeeTypeId, used to identify which of a user's 1-2 employment
  // relationships is the employee-category (HPP-like) one.
  const employeeTypeCategoryMap = useMemo(() => {
    const map = new Map<number, string | null>();
    for (const type of employeeTypes || []) {
      map.set(type.id, type.category ?? null);
    }
    return map;
  }, [employeeTypes]);

  // ===== Phase 71 (FTE-01/02, VAC-01..05/07, SBH-01) + 71-08 change-timeline =====
  const { data: employmentRelationships, isLoading: loadingRelationships } =
    useEmploymentRelationships(userId);
  const recordChangeMutation = useRecordEmploymentChange(userId);
  const deleteRelationshipMutation = useDeleteEmploymentRelationship(userId);

  // 71-08: employment is a timeline of dated changes. The top control records a
  // change (single type OR souběh); history (ended arrangements) lives behind a
  // toggle that lazily fetches the full timeline.
  const [showChangeForm, setShowChangeForm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [changeForm, setChangeForm] = useState<{
    mode: 'single' | 'concurrent';
    effectiveFrom: string;
    leg1: { employeeTypeId: string; fte: string };
    leg2: { employeeTypeId: string; fte: string };
  }>({
    mode: 'single',
    effectiveFrom: todayStr,
    leg1: { employeeTypeId: '', fte: '1.0' },
    leg2: { employeeTypeId: '', fte: '0.5' },
  });
  const [relationshipError, setRelationshipError] = useState<string | null>(null);

  const { data: relationshipHistory, isLoading: loadingHistory } = useEmploymentRelationships(
    userId,
    { history: true, enabled: showHistory }
  );

  // The one active relationship whose EmployeeType.category === 'employee'
  // (HPP-like) — the FTE-02 cost-leg repoint (Task 3) reads this relationship's
  // FTE + its EmployeeType.totalHoursPerMonth instead of the legacy
  // UserRate.partTime field + a hardcoded 167-hour average.
  const activeEmployeeRelationship = useMemo(() => {
    if (!employmentRelationships) return null;
    return (
      employmentRelationships.find(
        (r) => r.employeeTypeId != null && employeeTypeCategoryMap.get(r.employeeTypeId) === 'employee'
      ) ?? null
    );
  }, [employmentRelationships, employeeTypeCategoryMap]);

  const activeEmployeeTypeHoursPerMonth = useMemo(() => {
    if (!activeEmployeeRelationship?.employeeTypeId) return null;
    return employeeTypes?.find((t) => t.id === activeEmployeeRelationship.employeeTypeId)?.totalHoursPerMonth ?? null;
  }, [activeEmployeeRelationship, employeeTypes]);

  const resetChangeForm = () =>
    setChangeForm({
      mode: 'single',
      effectiveFrom: todayStr,
      leg1: { employeeTypeId: '', fte: '1.0' },
      leg2: { employeeTypeId: '', fte: '0.5' },
    });

  // 71-08: recording a change closes the current arrangement and opens the new
  // 1-2 legs, effective from the chosen date (backend does it atomically).
  const handleRecordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setRelationshipError(null);
    const legs = [
      { employeeTypeId: Number(changeForm.leg1.employeeTypeId), fte: parseFloat(changeForm.leg1.fte) },
    ];
    if (changeForm.mode === 'concurrent') {
      legs.push({
        employeeTypeId: Number(changeForm.leg2.employeeTypeId),
        fte: parseFloat(changeForm.leg2.fte),
      });
    }
    try {
      await recordChangeMutation.mutateAsync({ effectiveFrom: changeForm.effectiveFrom, legs });
      setShowChangeForm(false);
      resetChangeForm();
    } catch (err) {
      setRelationshipError((err as Error).message);
    }
  };

  const handleDeleteRelationship = async (rel: NonNullable<typeof employmentRelationships>[number]) => {
    if (!window.confirm('Delete this employment entry? This is a correction and cannot be undone.')) return;
    setRelationshipError(null);
    try {
      await deleteRelationshipMutation.mutateAsync(rel.id);
    } catch (err) {
      setRelationshipError((err as Error).message);
    }
  };

  const describeRelationship = (rel: NonNullable<typeof employmentRelationships>[number]): string => {
    const typeName =
      rel.employeeTypeId != null
        ? employeeTypeMap.get(rel.employeeTypeId) || `#${rel.employeeTypeId}`
        : 'Unspecified';
    return `${typeName} · ${rel.fte} FTE`;
  };

  // Phase 84 follow-up — roster for the buddy picker.
  const { data: allUsers } = useAdminUsers(false);

  const [balanceYear, setBalanceYear] = useState(new Date().getFullYear());
  const { data: vacationBalance, isLoading: loadingBalance } = useVacationBalance(userId, balanceYear);

  // Phase 84 — three amounts + which one the invoice deducts, per month.
  const [salaryPeriod, setSalaryPeriod] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  });
  const [salaryForm, setSalaryForm] = useState({
    netAmount: '',
    grossAmount: '',
    totalCostAmount: '',
    deductedField: 'gross' as DeductedField,
  });
  const [salarySavedMessage, setSalarySavedMessage] = useState<string | null>(null);
  const [salaryError, setSalaryError] = useState<string | null>(null);
  const [showSalaryHistory, setShowSalaryHistory] = useState(false);

  const { data: salaryRecord } = useEmployeeSalary(userId, salaryPeriod.year, salaryPeriod.month, {
    enabled: canSeeSalary,
  });
  const { data: salaryHistory, isLoading: loadingSalaryHistory } = useEmployeeSalaryHistory(
    userId,
    salaryPeriod.year,
    salaryPeriod.month,
    { enabled: canSeeSalary && showSalaryHistory }
  );
  const setSalaryMutation = useSetEmployeeSalary(userId);

  // Reload the form whenever the selected month changes so the inputs always
  // show what is actually stored — an editor that keeps the previous month's
  // figures invites overwriting the wrong month.
  useEffect(() => {
    setSalarySavedMessage(null);
    setSalaryError(null);
    setSalaryForm({
      netAmount: salaryRecord?.netAmount != null ? String(salaryRecord.netAmount) : '',
      grossAmount: salaryRecord?.grossAmount != null ? String(salaryRecord.grossAmount) : '',
      totalCostAmount:
        salaryRecord?.totalCostAmount != null ? String(salaryRecord.totalCostAmount) : '',
      deductedField: salaryRecord?.deductedField ?? 'gross',
    });
  }, [salaryRecord, salaryPeriod.year, salaryPeriod.month]);

  const parseAmount = (raw: string): number | null =>
    raw.trim() === '' ? null : parseFloat(raw);

  const salaryAmounts = {
    netAmount: parseAmount(salaryForm.netAmount),
    grossAmount: parseAmount(salaryForm.grossAmount),
    totalCostAmount: parseAmount(salaryForm.totalCostAmount),
  };
  // `none` names no amount, so there is nothing to validate against.
  const amountForSelectedField =
    salaryForm.deductedField === 'net'
      ? salaryAmounts.netAmount
      : salaryForm.deductedField === 'gross'
        ? salaryAmounts.grossAmount
        : salaryForm.deductedField === 'total_cost'
          ? salaryAmounts.totalCostAmount
          : 0;
  const allSalaryAmountsEmpty =
    salaryAmounts.netAmount === null &&
    salaryAmounts.grossAmount === null &&
    salaryAmounts.totalCostAmount === null;
  // The server rejects a deducted field whose amount is missing; block it here
  // too rather than letting the user submit a guaranteed error.
  const salaryDeductionInvalid = !allSalaryAmountsEmpty && amountForSelectedField === null;

  const submitSalary = () => {
    setSalarySavedMessage(null);
    setSalaryError(null);
    setSalaryMutation.mutate(
      {
        year: salaryPeriod.year,
        month: salaryPeriod.month,
        input: { ...salaryAmounts, deductedField: salaryForm.deductedField },
      },
      {
        onSuccess: (data) => {
          setSalarySavedMessage(
            data
              ? data.deductedField === 'none'
                ? `Saved ${salaryPeriod.month}/${salaryPeriod.year} — recorded, nothing deducted from the invoice.`
                : `Saved ${salaryPeriod.month}/${salaryPeriod.year} — deducting ${DEDUCTED_FIELD_LABELS[data.deductedField].toLowerCase()} ${formatCurrency(data.deductedAmount ?? 0)}.`
              : `Cleared salary for ${salaryPeriod.month}/${salaryPeriod.year} (0 deduction).`
          );
        },
        onError: (err: Error) => setSalaryError(err.message || 'Failed to save salary'),
      }
    );
  };

  const activeAssignmentsOnly = useMemo(() => {
    if (!assignments) return [];
    return assignments.filter((a) => {
      if (a.deleted_at) return false;
      if (!a.endDate) return true;
      return a.endDate.slice(0, 10) >= todayStr;
    });
  }, [assignments, todayStr]);

  const filteredAssignments = useMemo(() => {
    if (!assignments) return [];
    if (showPastAssignments) return assignments;
    return activeAssignmentsOnly;
  }, [assignments, showPastAssignments, activeAssignmentsOnly]);

  // Group assignments by project: active first, then past as sub-items
  const groupedAssignments = useMemo(() => {
    const isPast = (a: typeof filteredAssignments[0]) =>
      !!a.deleted_at || (a.endDate != null && a.endDate.slice(0, 10) < todayStr);

    // Group by projectId
    const groups = new Map<number, { active: typeof filteredAssignments; past: typeof filteredAssignments }>();
    for (const a of filteredAssignments) {
      const pid = a.projectId;
      if (!groups.has(pid)) groups.set(pid, { active: [], past: [] });
      const group = groups.get(pid)!;
      if (isPast(a)) {
        group.past.push(a);
      } else {
        group.active.push(a);
      }
    }

    // Sort past by startDate descending within each group
    for (const group of groups.values()) {
      group.past.sort((a, b) => (b.startDate || '').localeCompare(a.startDate || ''));
    }

    // Build flat list: active items first (sorted), then their past sub-items
    const { key, dir } = assignmentSort;
    const mult = dir === 'asc' ? 1 : -1;
    const sortVal = (a: typeof filteredAssignments[0]) => {
      if (key === 'project') return (a.project?.fullName || a.project?.name || '').toLowerCase();
      if (key === 'startDate') return a.startDate || '';
      return a.endDate || '';
    };

    // Separate groups with active items from past-only groups
    const activeGroups = [...groups.entries()].filter(([, g]) => g.active.length > 0);
    const pastOnlyGroups = [...groups.entries()].filter(([, g]) => g.active.length === 0);

    // Sort each set independently
    const projectName = (a: typeof filteredAssignments[0]) =>
      (a.project?.fullName || a.project?.name || '').toLowerCase();

    const sortGroups = (list: typeof activeGroups) =>
      list.sort(([, ga], [, gb]) => {
        const repA = ga.active[0] || ga.past[0];
        const repB = gb.active[0] || gb.past[0];
        if (!repA || !repB) return 0;
        const cmp = mult * sortVal(repA).localeCompare(sortVal(repB));
        if (cmp !== 0) return cmp;
        // Secondary sort: project name ascending
        return projectName(repA).localeCompare(projectName(repB));
      });

    sortGroups(activeGroups);
    sortGroups(pastOnlyGroups);

    const result: Array<{ assignment: typeof filteredAssignments[0]; isPast: boolean; isSubItem: boolean }> = [];

    // Active groups first
    for (const [, group] of activeGroups) {
      for (const a of group.active) {
        result.push({ assignment: a, isPast: false, isSubItem: false });
      }
      if (showPastAssignments) {
        for (const a of group.past) {
          result.push({ assignment: a, isPast: true, isSubItem: true });
        }
      }
    }

    // Past-only groups at the end: first past item as header, rest as sub-items
    if (showPastAssignments) {
      for (const [, group] of pastOnlyGroups) {
        const [header, ...rest] = group.past;
        if (header) {
          result.push({ assignment: header, isPast: true, isSubItem: false });
          for (const a of rest) {
            result.push({ assignment: a, isPast: true, isSubItem: true });
          }
        }
      }
    }

    return result;
  }, [filteredAssignments, assignmentSort, showPastAssignments, todayStr]);

  const sortedRates = useMemo(() => {
    if (!rates) return [];
    return [...rates].sort((a, b) => b.validFrom.localeCompare(a.validFrom));
  }, [rates]);

  const currentRate = useMemo(() => {
    return sortedRates.find((r) => !r.validTo) ?? null;
  }, [sortedRates]);

  const filteredRates = useMemo(() => {
    // Exclude invalid rates where validFrom > validTo
    const validRates = sortedRates.filter((r) => {
      if (!r.validTo) return true;
      return r.validFrom.slice(0, 10) <= r.validTo.slice(0, 10);
    });
    if (showRateHistory) return validRates;
    const today = formatLocalDate(new Date());
    return validRates.filter((r) => {
      if (!r.validTo) return true;
      return r.validTo.slice(0, 10) >= today;
    });
  }, [sortedRates, showRateHistory]);

  const createRateMutation = useMutation({
    mutationFn: (data: Parameters<typeof createUserRate>[1]) => createUserRate(userId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userRates', userId] });
      queryClient.invalidateQueries({ queryKey: ['adminUser', userId] });
      queryClient.invalidateQueries({ queryKey: ['userAssignments', userId] });
      queryClient.invalidateQueries({ queryKey: ['auditTrail', 'User', userId] });
      setActionError(null);
      setRateModalOpen(false);
    },
    onError: (err: Error) => setActionError(err.message || 'Failed to create rate'),
  });

  const deleteRateMutation = useMutation({
    mutationFn: (rateId: number) => deleteUserRate(userId!, rateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userRates', userId] });
      queryClient.invalidateQueries({ queryKey: ['adminUser', userId] });
      queryClient.invalidateQueries({ queryKey: ['userAssignments', userId] });
      queryClient.invalidateQueries({ queryKey: ['auditTrail', 'User', userId] });
      setActionError(null);
      setHighlightedRateId(null);
    },
    onError: (err: Error) => setActionError(err.message || 'Failed to delete rate'),
  });

  const updateAssignmentMutation = useMutation({
    mutationFn: ({ projectId, assignmentId, input }: { projectId: number; assignmentId: number; input: UpdateAssignmentInput }) =>
      updateAssignment(projectId, assignmentId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userAssignments', userId] });
      queryClient.invalidateQueries({ queryKey: ['auditTrail', 'User', userId] });
      setActionError(null);
      setEditingAssignmentId(null);
    },
    onError: (err: Error) => setActionError(err.message || 'Failed to update assignment'),
  });

  const deleteAssignmentMutation = useMutation({
    mutationFn: ({ projectId, assignmentId }: { projectId: number; assignmentId: number }) =>
      deleteAssignment(projectId, assignmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userAssignments', userId] });
      queryClient.invalidateQueries({ queryKey: ['auditTrail', 'User', userId] });
      setActionError(null);
      setDeleteAssignmentId(null);
    },
    onError: (err: Error) => setActionError(err.message || 'Failed to delete assignment'),
  });


  const handleEditStart = (u: AdminUser) => {
    setEditForm({
      firstName: u.firstName || '',
      lastName: u.lastName || '',
      roles: normalizeRoles(u.roles),
      employeeTypeId: u.employeeTypeId,
      // Phase 46 staging UAT 2026-05-21: backend userBodySchema validates
      // startDate/endDate as JSON Schema format:'date' (= YYYY-MM-DD). DB
      // stores ISO timestamps (e.g. '2024-01-15T00:00:00.000Z') which the
      // input field displays via `?.split('T')[0]`. If the admin doesn't touch
      // the date inputs, editForm carried the raw ISO string and the PUT
      // failed with "body/startDate must match format 'date'". Normalize at
      // load time so the form value is always YYYY-MM-DD.
      startDate: u.startDate ? u.startDate.split('T')[0] : null,
      endDate: u.endDate ? u.endDate.split('T')[0] : null,
      // Quick 260701-eor: normalize birthDate to YYYY-MM-DD at load time so
      // the form value satisfies format:'date' on PUT (same fix as startDate
      // above — Quick 260525-k1l).
      birthDate: u.birthDate ? u.birthDate.split('T')[0] : null,
      githubUsername: u.githubUsername ?? null,
      buddyId: u.buddyId ?? null,
      slackUserId: u.slackUserId ?? null,
      phoneNumber: u.phoneNumber ?? null,
      // Phase 71 (VAC-01, D-05) — entitlement days + the manual carry-in
      // seed. Both round-trip through the same admin user PUT as every
      // other field on this form.
      vacationEntitlementDays: u.vacationEntitlementDays,
      vacationCarryInSeed: u.vacationCarryInSeed,
    });
    setEditError(null);
    setIsEditing(true);
  };

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setEditError(null);
    try {
      // Phase 27 WR-05 + Phase 46 staging UAT 2026-05-21: backend
      // userBodySchema enum only accepts the 10 D-04 canonical roles. The FE
      // renders 'system_administrator' and 'robot' as disabled checkboxes
      // (service accounts) and PRE-Phase-27 users in the DB may still carry
      // legacy values like 'employee' that were never aliased. Strip ANY role
      // not in the backend enum (BACKEND_ROLE_ENUM) before PUT so an admin
      // editing contact-info (slackHandle, phoneNumber) on such a user
      // doesn't get a 400.
      //
      // If the strip leaves the roles array empty, default to ['user'] so we
      // don't accidentally save a user with zero roles (= no permissions, no
      // login access). Manage service accounts via DB.
      //
      // Note: contact-info fields (slackUserId, phoneNumber) are passed
      // through unchanged; only roles are sanitized.
      let sanitizedInput: UpdateUserInput = editForm;
      if (editForm.roles) {
        const validRoles = editForm.roles.filter((r) =>
          (BACKEND_ROLE_ENUM as readonly string[]).includes(r)
        );
        const finalRoles = validRoles.length > 0 ? validRoles : ['user'];
        sanitizedInput = { ...editForm, roles: finalRoles };
      }
      // Quick 260525-k1l: defensive date normalization. Belt-and-suspenders
      // against ISO-datetime leaking into the PUT body (the root cause behind
      // MKS-93 and 46-W4). Reduces anything that isn't a strict YYYY-MM-DD
      // string to null — backend schema now accepts null for both fields.
      sanitizedInput = {
        ...sanitizedInput,
        startDate: toYmdOrNull(sanitizedInput.startDate),
        endDate: toYmdOrNull(sanitizedInput.endDate),
        // Quick 260701-eor: belt-and-suspenders normalization for birthDate.
        // Mirrors startDate/endDate — reduces any non-YYYY-MM-DD to null so
        // the PUT body never carries a raw ISO datetime for this field.
        birthDate: toYmdOrNull(sanitizedInput.birthDate),
      };
      await updateMutation.mutateAsync({ id: user.id, input: sanitizedInput });
      queryClient.invalidateQueries({ queryKey: ['adminUser', userId] });
      queryClient.invalidateQueries({ queryKey: ['auditTrail', 'User', userId] });
      setIsEditing(false);
    } catch (err) {
      setEditError((err as Error).message);
    }
  };

  const handleRoleToggle = (role: string) => {
    const currentRoles = editForm.roles || [];
    if (currentRoles.includes(role)) {
      setEditForm((prev) => ({ ...prev, roles: currentRoles.filter((r) => r !== role) }));
    } else {
      setEditForm((prev) => ({ ...prev, roles: [...currentRoles, role] }));
    }
  };

  if (loadingUser) {
    return <div className="flex justify-center py-8"><Spinner /></div>;
  }

  if (userError || !user) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">
          {userError ? `Failed to load user: ${(userError as Error).message}` : 'User not found.'}
        </p>
        <Link to="/admin/users" className="text-primary-600 hover:underline mt-2 inline-block">
          Back to users
        </Link>
      </div>
    );
  }

  const displayName = user.lastName && user.firstName
    ? `${user.lastName}, ${user.firstName}`
    : user.lastName || user.firstName || user.email;

  return (
    <div className="space-y-6">
      {/* F4-002 — Surface mutation rejections (create/delete rate,
          update/delete assignment) so the user sees feedback instead of
          a silently stuck modal. */}
      <DismissibleErrorBanner message={actionError} onDismiss={() => setActionError(null)} />

      {/* Header */}
      <div>
        <button onClick={() => navigate('/admin/users')} className="text-sm text-gray-500 hover:text-gray-700 mb-1">
          &larr; Back to Users
        </button>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-gray-900">{displayName}</h1>
            <span className="text-sm text-gray-400">{user.email}</span>
            {user.deletedAt ? (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                Deleted
              </span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                Active
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {user.deletedAt && (
              <Button
                variant="secondary"
                onClick={() => { setSkipOnboarding(false); setRestoreTier('claude-standard'); setShowRestoreConfirm(true); }}
              >
                Restore
              </Button>
            )}
            {!user.deletedAt && !isEditing && (
              <>
                {currentUser && currentUser.id !== user.id && (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      startImpersonating({
                        id: user.id,
                        email: user.email,
                        firstName: user.firstName,
                        lastName: user.lastName,
                      });
                      navigate('/dashboard');
                    }}
                  >
                    Act as this user
                  </Button>
                )}
                <Button variant="secondary" onClick={() => handleEditStart(user)}>Edit</Button>
                <Button variant="danger" onClick={() => setShowArchiveConfirm(true)}>Archive</Button>
              </>
            )}
            {isEditing && (
              <Button variant="ghost" onClick={() => setIsEditing(false)}>Cancel</Button>
            )}
          </div>
        </div>
      </div>

      {/* Section 1: User Info */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">User Info</h2>

        {isEditing ? (
          <form onSubmit={handleEditSave} className="space-y-4">
            {editError && <p className="text-sm text-red-600">{editError}</p>}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  value={editForm.firstName || ''}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, firstName: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  value={editForm.lastName || ''}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, lastName: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="text"
                value={user.email}
                disabled
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employee Type</label>
              <select
                value={editForm.employeeTypeId ?? ''}
                onChange={(e) => setEditForm((prev) => ({
                  ...prev,
                  employeeTypeId: e.target.value ? Number(e.target.value) : null,
                }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">-- None --</option>
                {employeeTypes?.map((type) => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Roles</label>
              <div className="flex flex-wrap gap-2">
                {/*
                  Phase 27 D-17 + Pitfall #6: render checkboxes from
                  `availableRoles` (single source of truth in
                  `frontend/src/utils/roles.ts`). The 5 D-04 additions
                  (client_manager, finance, hr, marketing, isms_manager)
                  appear automatically. NEVER hardcode role values here.

                  Legacy values (system_administrator, robot) are kept in
                  `availableRoles` defensively so existing service accounts
                  do not get silently stripped, but the backend enum
                  (Plan 27-04) does NOT accept them on POST/PUT. To avoid
                  a confusing 400 we render legacy checkboxes disabled
                  unless they are already checked — admins can clean up
                  existing assignments but cannot newly assign a legacy
                  value via UI (would have to be done in DB).
                */}
                {availableRoles.map((role) => {
                  const isLegacy = role.value === 'system_administrator' || role.value === 'robot';
                  const isChecked = editForm.roles?.includes(role.value) || false;
                  const disabled = isLegacy && !isChecked;
                  return (
                    <label
                      key={role.value}
                      className={`inline-flex items-center gap-1.5 text-sm ${disabled ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      title={disabled ? 'Legacy role — assignable only via DB' : undefined}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={disabled}
                        onChange={() => handleRoleToggle(role.value)}
                        className="rounded border-gray-300"
                      />
                      {role.label}
                      {isLegacy && <span className="text-xs text-gray-400">(legacy)</span>}
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <DateField
                  value={editForm.startDate?.split('T')[0] || ''}
                  onChange={(nextDate) => setEditForm((prev) => ({ ...prev, startDate: nextDate || null }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <DateField
                  value={editForm.endDate?.split('T')[0] || ''}
                  onChange={(nextDate) => setEditForm((prev) => ({ ...prev, endDate: nextDate || null }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Birth Date</label>
                <DateField
                  value={editForm.birthDate?.split('T')[0] || ''}
                  onChange={(nextDate) => setEditForm((prev) => ({ ...prev, birthDate: nextDate || null }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vacation Entitlement (days/year)
                </label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={editForm.vacationEntitlementDays ?? ''}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      vacationEntitlementDays: e.target.value === '' ? null : Number(e.target.value),
                    }))
                  }
                  placeholder="20"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
                <p className="text-[11px] text-gray-500 mt-1">Default 20. Drives the vacation balance below.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prior-year carryover (hours)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={editForm.vacationCarryInSeed ?? ''}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      vacationCarryInSeed: e.target.value === '' ? null : Number(e.target.value),
                    }))
                  }
                  placeholder="e.g. 24.5 (leave blank if none)"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Manual one-time seed of this person&apos;s unused vacation hours carried over from BEFORE
                  the introduction year. Without this, their pre-2026 carryover defaults to 0.
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">GitHub Username</label>
              <input
                type="text"
                value={editForm.githubUsername || ''}
                onChange={(e) => setEditForm((prev) => ({ ...prev, githubUsername: e.target.value || null }))}
                placeholder="e.g. octocat"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>

            {/* Phase 84 follow-up — onboarding buddy. Editable after creation:
                buddies change, and the person who set it up is rarely the one
                who notices. The user themselves is excluded from the list. */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Buddy</label>
              <select
                value={editForm.buddyId ?? ''}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    buddyId: e.target.value === '' ? null : Number(e.target.value),
                  }))
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">No buddy</option>
                {(allUsers ?? [])
                  .filter((u) => !u.deletedAt && u.id !== user.id)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {[u.lastName, u.firstName].filter(Boolean).join(' ') || u.email}
                    </option>
                  ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Slack User ID</label>
                <input
                  type="text"
                  value={editForm.slackUserId || ''}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, slackUserId: e.target.value || null }))}
                  placeholder="e.g. U07PP72S8GJ"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input
                  type="text"
                  value={editForm.phoneNumber || ''}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, phoneNumber: e.target.value || null }))}
                  placeholder="+420123456789"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
                <p className="text-[11px] text-gray-500 mt-1">E.164 format with leading + and country code.</p>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button type="submit" isLoading={updateMutation.isPending}>
                Save Changes
              </Button>
            </div>
          </form>
        ) : (
          <dl className="grid grid-cols-2 gap-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">First Name</dt>
              <dd className="text-sm text-gray-900 mt-1">{user.firstName || '-'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Last Name</dt>
              <dd className="text-sm text-gray-900 mt-1">{user.lastName || '-'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Email</dt>
              <dd className="text-sm text-gray-900 mt-1">{user.email}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Employee Type</dt>
              <dd className="text-sm text-gray-900 mt-1">
                {user.employeeTypeId ? employeeTypeMap.get(user.employeeTypeId) || '-' : '-'}
              </dd>
            </div>
            <div className="col-span-2">
              <dt className="text-sm font-medium text-gray-500">Roles</dt>
              <dd className="text-sm text-gray-900 mt-1">
                {user.roles.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {user.roles.map((role) => {
                      const normalized = roleAliasMap[role] ?? role;
                      return (
                        <span
                          key={role}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {availableRoles.find((r) => r.value === normalized)?.label || role}
                        </span>
                      );
                    })}
                  </div>
                ) : '-'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Start Date</dt>
              <dd className="text-sm text-gray-900 mt-1">{formatDisplayDate(user.startDate)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">End Date</dt>
              <dd className="text-sm text-gray-900 mt-1">{formatDisplayDate(user.endDate)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Birth Date</dt>
              <dd className="text-sm text-gray-900 mt-1">{formatDisplayDate(user.birthDate)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">GitHub</dt>
              <dd className="text-sm text-gray-900 mt-1">{user.githubUsername || '-'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Buddy</dt>
              <dd className="text-sm text-gray-900 mt-1">
                {user.buddy
                  ? [user.buddy.lastName, user.buddy.firstName].filter(Boolean).join(' ') ||
                    user.buddy.email
                  : '-'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Slack User ID</dt>
              <dd className="text-sm text-gray-900 mt-1">{user.slackUserId || '-'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Phone Number</dt>
              <dd className="text-sm text-gray-900 mt-1">{user.phoneNumber || '-'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Vacation Entitlement</dt>
              <dd className="text-sm text-gray-900 mt-1">
                {user.vacationEntitlementDays != null ? `${user.vacationEntitlementDays} days/year` : '-'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Prior-year Carryover</dt>
              <dd className="text-sm text-gray-900 mt-1">
                {user.vacationCarryInSeed != null ? `${user.vacationCarryInSeed} h` : 'Not set (defaults to 0)'}
              </dd>
            </div>
          </dl>
        )}
      </div>

      {/* MKS-393 — Merch sizes. A card of its own against the narrow
          requireHR endpoint, NOT fields on the user form above: that form's
          PUT /api/admin/users/:id is and stays requireAdmin. */}
      {userId && <UserMerchSizesCard userId={userId} />}

      {/* Section: Employment Relationships (Phase 71, FTE-01/FTE-02, D-01) */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Employment</h2>
            <p className="text-sm text-gray-500 mt-1">
              A timeline of dated changes. At any time a person is on a single type or in concurrent
              employment (up to 2 distinct types). Recording a change closes the current arrangement and
              opens the new one. Capacity FTE = sum of active legs&apos; FTE.
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => {
              setRelationshipError(null);
              resetChangeForm();
              setShowChangeForm((v) => !v);
            }}
          >
            {showChangeForm ? 'Cancel' : 'Record change'}
          </Button>
        </div>

        {relationshipError && <p className="text-sm text-red-600 mb-3">{relationshipError}</p>}

        {/* 71-08: record a change (single type or concurrent) */}
        {showChangeForm && (
          <form onSubmit={handleRecordChange} className="mb-4 border border-gray-200 rounded-lg p-4 space-y-4">
            <div className="flex items-center gap-6">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="arrangement"
                  checked={changeForm.mode === 'single'}
                  onChange={() => setChangeForm((p) => ({ ...p, mode: 'single' }))}
                />
                Single type
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="arrangement"
                  checked={changeForm.mode === 'concurrent'}
                  onChange={() => setChangeForm((p) => ({ ...p, mode: 'concurrent' }))}
                />
                Concurrent employment
              </label>
            </div>

            {/* Leg 1 */}
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Employee type</label>
                <select
                  value={changeForm.leg1.employeeTypeId}
                  onChange={(e) =>
                    setChangeForm((p) => ({ ...p, leg1: { ...p.leg1, employeeTypeId: e.target.value } }))
                  }
                  className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                  required
                >
                  <option value="">-- Select --</option>
                  {employeeTypes?.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                      {type.category ? ` (${type.category})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">FTE</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max="2.00"
                  value={changeForm.leg1.fte}
                  onChange={(e) =>
                    setChangeForm((p) => ({ ...p, leg1: { ...p.leg1, fte: e.target.value } }))
                  }
                  className="w-24 rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                  required
                />
              </div>
            </div>

            {/* Leg 2 (concurrent only) */}
            {changeForm.mode === 'concurrent' && (
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Second type</label>
                  <select
                    value={changeForm.leg2.employeeTypeId}
                    onChange={(e) =>
                      setChangeForm((p) => ({ ...p, leg2: { ...p.leg2, employeeTypeId: e.target.value } }))
                    }
                    className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                    required
                  >
                    <option value="">-- Select --</option>
                    {employeeTypes?.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                        {type.category ? ` (${type.category})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">FTE</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max="2.00"
                    value={changeForm.leg2.fte}
                    onChange={(e) =>
                      setChangeForm((p) => ({ ...p, leg2: { ...p.leg2, fte: e.target.value } }))
                    }
                    className="w-24 rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                    required
                  />
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Effective from</label>
                <DateField
                  value={changeForm.effectiveFrom}
                  onChange={(nextDate) => setChangeForm((p) => ({ ...p, effectiveFrom: nextDate }))}
                  className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                  required
                />
              </div>
              <Button type="submit" size="sm" isLoading={recordChangeMutation.isPending}>
                Save change
              </Button>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => {
                  setShowChangeForm(false);
                  resetChangeForm();
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}

        {/* Current arrangement */}
        {loadingRelationships ? (
          <div className="flex justify-center py-4"><Spinner /></div>
        ) : !employmentRelationships?.length ? (
          <p className="text-sm text-gray-500">No active employment.</p>
        ) : (
          <div className="rounded-lg border border-gray-200 p-4">
            <div className="text-xs font-medium text-gray-500 uppercase mb-2">Current arrangement</div>
            <ul className="space-y-1">
              {employmentRelationships.map((rel) => (
                <li key={rel.id} className="text-sm text-gray-900 flex flex-wrap items-center gap-2">
                  <span className="font-medium">{describeRelationship(rel)}</span>
                  {rel.employeeTypeId != null && employeeTypeCategoryMap.get(rel.employeeTypeId) && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                      {employeeTypeCategoryMap.get(rel.employeeTypeId)}
                    </span>
                  )}
                  <span className="text-gray-400">· since {formatDisplayDate(rel.validFrom)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* History (ended arrangements) */}
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowHistory((v) => !v)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {showHistory ? '▾ Hide history' : '▸ Show history'}
          </button>
          {showHistory &&
            (loadingHistory ? (
              <div className="flex justify-center py-4"><Spinner /></div>
            ) : !relationshipHistory?.length ? (
              <p className="text-sm text-gray-500 mt-2">No employment history.</p>
            ) : (
              <div className="mt-2 overflow-hidden rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee Type</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">FTE</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">From</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">To</th>
                      <th className="px-4 py-3 w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {relationshipHistory.map((rel) => {
                      const ended = !!rel.validTo;
                      return (
                        <tr key={rel.id} className={ended ? 'text-gray-400' : 'text-gray-900'}>
                          <td className="px-4 py-3 text-sm">
                            {rel.employeeTypeId != null
                              ? employeeTypeMap.get(rel.employeeTypeId) || `#${rel.employeeTypeId}`
                              : 'Unspecified'}
                            {rel.employeeTypeId != null && employeeTypeCategoryMap.get(rel.employeeTypeId) && (
                              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                                {employeeTypeCategoryMap.get(rel.employeeTypeId)}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-right">{rel.fte}</td>
                          <td className="px-4 py-3 text-sm">{formatDisplayDate(rel.validFrom)}</td>
                          <td className="px-4 py-3 text-sm">
                            {rel.validTo ? formatDisplayDate(rel.validTo) : 'Ongoing'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              title="Delete this entry (correction)"
                              className="text-red-500 hover:text-red-700 disabled:opacity-50"
                              disabled={deleteRelationshipMutation.isPending}
                              onClick={() => handleDeleteRelationship(rel)}
                            >
                              ×
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
        </div>
      </div>

      {/* Section: Vacation Balance (Phase 71, VAC-01..05/07, HR/admin-only) */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">Vacation Balance</h2>
          <div className="flex items-center gap-2">
            <button
              className="px-2 py-1 text-sm rounded border border-gray-200 hover:bg-gray-50"
              onClick={() => setBalanceYear((y) => y - 1)}
            >
              &larr;
            </button>
            <span className="text-sm font-medium text-gray-900 min-w-[3rem] text-center">{balanceYear}</span>
            <button
              className="px-2 py-1 text-sm rounded border border-gray-200 hover:bg-gray-50"
              onClick={() => setBalanceYear((y) => y + 1)}
            >
              &rarr;
            </button>
          </div>
        </div>

        {loadingBalance ? (
          <div className="flex justify-center py-4"><Spinner /></div>
        ) : vacationBalance?.entitlementHours == null ? (
          <p className="text-sm text-gray-500">
            No entitlement for {balanceYear} (contractor-only relationship, or no active employee-category
            relationship overlapping this year).
          </p>
        ) : (
          <dl className="grid grid-cols-4 gap-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Entitlement</dt>
              <dd className="text-sm text-gray-900 mt-1">{vacationBalance.entitlementHours.toFixed(2)} h</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Used</dt>
              <dd className="text-sm text-gray-900 mt-1">{(vacationBalance.usedHours ?? 0).toFixed(2)} h</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Carried-over</dt>
              <dd className="text-sm text-gray-900 mt-1">{(vacationBalance.carriedInHours ?? 0).toFixed(2)} h</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Remaining</dt>
              <dd className={`text-sm font-semibold mt-1 ${(vacationBalance.remainingHours ?? 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                {(vacationBalance.remainingHours ?? 0).toFixed(2)} h
              </dd>
            </div>
          </dl>
        )}
      </div>

      {/* Section: Monthly Salary (Phase 71 SBH-01 → Phase 84 → quick-260813-hob:
          finance-only, NOT HR/admin — canSeeSalary gates the whole card; the
          backend requireFinanceOnly gate is authoritative, this is a
          UI-level hide so a non-finance admin never triggers a 403). */}
      {canSeeSalary && (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-1">Monthly salary</h2>
        <p className="text-sm text-gray-500 mb-4">
          Record this person&apos;s salary for a month and pick which amount is deducted from their
          freelancer invoice (invoice = max(0, earnings − extra costs − deducted amount)). Super
          gross cost includes levies. A month with nothing entered means no deduction. Past months
          can be corrected at any time; every change is recorded below.
        </p>

        <form
          onSubmit={(e) => { e.preventDefault(); submitSalary(); }}
          className="space-y-4"
        >
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Year</label>
              <input
                type="number"
                value={salaryPeriod.year}
                onChange={(e) => setSalaryPeriod((p) => ({ ...p, year: Number(e.target.value) }))}
                className="w-24 rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Month</label>
              <select
                value={salaryPeriod.month}
                onChange={(e) => setSalaryPeriod((p) => ({ ...p, month: Number(e.target.value) }))}
                className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            {([
              { key: 'netAmount', field: 'net', label: 'Net salary' },
              { key: 'grossAmount', field: 'gross', label: 'Gross salary' },
              { key: 'totalCostAmount', field: 'total_cost', label: 'Super gross cost' },
            ] as const).map(({ key, field, label }) => (
              <div key={key}>
                <label className="block text-xs font-medium text-gray-700 mb-1">{label} (Kč)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={salaryForm[key]}
                  onChange={(e) => setSalaryForm((prev) => ({ ...prev, [key]: e.target.value }))}
                  placeholder="—"
                  className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                />
                <label className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-600">
                  <input
                    type="radio"
                    name="deductedField"
                    value={field}
                    checked={salaryForm.deductedField === field}
                    onChange={() => setSalaryForm((prev) => ({ ...prev, deductedField: field }))}
                  />
                  Deduct this from the invoice
                </label>
              </div>
            ))}
          </div>

          {/* `none` belongs to no column, so it sits below the grid rather than
              inside one. For someone who draws a salary AND bills every
              contractor hour on top, the two are independent income — the
              salary is recorded for cost reporting only. */}
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="radio"
              name="deductedField"
              value="none"
              checked={salaryForm.deductedField === 'none'}
              onChange={() => setSalaryForm((prev) => ({ ...prev, deductedField: 'none' }))}
            />
            Deduct nothing — record the salary but leave the invoice untouched
          </label>

          {salaryDeductionInvalid && (
            <p className="text-sm text-amber-700">
              Enter a {DEDUCTED_FIELD_LABELS[salaryForm.deductedField].toLowerCase()} amount, or pick
              a different amount to deduct.
            </p>
          )}
          {salaryError && <p className="text-sm text-red-600">{salaryError}</p>}
          {salarySavedMessage && <p className="text-sm text-green-700">{salarySavedMessage}</p>}

          <div className="flex items-center gap-3">
            <Button
              type="submit"
              size="sm"
              isLoading={setSalaryMutation.isPending}
              disabled={salaryDeductionInvalid}
            >
              {allSalaryAmountsEmpty ? 'Clear month' : 'Save salary'}
            </Button>
            {salaryRecord && (
              <span className="text-xs text-gray-500">
                {salaryRecord.deductedField === 'none'
                  ? 'Recorded — nothing deducted from the invoice'
                  : `Currently deducting ${DEDUCTED_FIELD_LABELS[salaryRecord.deductedField].toLowerCase()} ${formatCurrency(salaryRecord.deductedAmount ?? 0)}`}
              </span>
            )}
          </div>
        </form>

        {/* Edit history sits BELOW the editor — the primary action is entering
            this month's figures; the audit trail is secondary reading. */}
        <div className="mt-5 border-t border-gray-200 pt-4">
          <button
            type="button"
            onClick={() => setShowSalaryHistory((v) => !v)}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            {showSalaryHistory ? '▾' : '▸'} Change history for {salaryPeriod.month}/{salaryPeriod.year}
          </button>

          {showSalaryHistory && (
            <div className="mt-3 space-y-2">
              {loadingSalaryHistory && <p className="text-sm text-gray-500">Loading…</p>}
              {!loadingSalaryHistory && (salaryHistory?.length ?? 0) === 0 && (
                <p className="text-sm text-gray-500">No changes recorded for this month.</p>
              )}
              {salaryHistory?.map((entry) => (
                <div key={entry.id} className="rounded-lg bg-gray-50 px-3 py-2 text-sm">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    <span className="font-medium text-gray-700">{entry.state}</span>
                    <span>{new Date(entry.createdAt).toLocaleString()}</span>
                    {entry.changes.editedClosedMonth && (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-800">
                        edited after the invoice was {entry.changes.invoiceState}
                      </span>
                    )}
                  </div>
                  <SalaryHistoryDetail entry={entry} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Section 2: Rate History */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">Rates</h2>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={showRateHistory}
                onChange={(e) => setShowRateHistory(e.target.checked)}
                className="rounded border-gray-300"
              />
              Show history
            </label>
            <Button
              size="sm"
              onClick={() => setRateModalOpen(true)}
            >
              Update Rate
            </Button>
          </div>
        </div>

        {loadingRates ? (
          <div className="flex justify-center py-4"><Spinner /></div>
        ) : filteredRates.length === 0 ? (
          <p className="text-sm text-gray-500">No rates defined.</p>
        ) : (
          <>
          {/* Timeline chart */}
          {(() => {
            // Build timeline from rates sorted by validFrom ascending
            const timelineRates = [...filteredRates].sort((a, b) => a.validFrom.localeCompare(b.validFrom));
            if (timelineRates.length === 0) return null;

            const parseDate = (d: string) => new Date(d.split('T')[0] + 'T00:00:00');
            const firstRate = timelineRates[0]!;
            const firstDate = parseDate(firstRate.validFrom);
            // End: either last rate's validTo, or 3 months from today for open-ended
            const today = new Date();
            const defaultEnd = new Date(today);
            defaultEnd.setMonth(defaultEnd.getMonth() + 3);
            const lastRate = timelineRates[timelineRates.length - 1]!;
            const lastEnd = lastRate.validTo ? parseDate(lastRate.validTo) : defaultEnd;
            const endDate = lastEnd > defaultEnd ? lastEnd : defaultEnd;
            const totalMs = endDate.getTime() - firstDate.getTime();
            if (totalMs <= 0) return null;

            // Generate labels at rate change dates only
            const dateLabels: { label: string; position: number }[] = [];
            for (const rate of timelineRates) {
              const d = parseDate(rate.validFrom);
              const pos = ((d.getTime() - firstDate.getTime()) / totalMs) * 100;
              if (pos >= 0 && pos <= 100) {
                dateLabels.push({
                  label: formatDayMonthShort(d), // MKS-351 — "20 Nov"
                  position: pos,
                });
              }
            }

            // Color palette for rate segments
            const colors = [
              'bg-blue-400', 'bg-emerald-400', 'bg-amber-400', 'bg-purple-400',
              'bg-rose-400', 'bg-cyan-400', 'bg-orange-400', 'bg-indigo-400',
            ];
            // Assign colors by unique rate value
            const uniqueRates = [...new Set(timelineRates.map(r => r.hourlyCosts ?? 0))];
            const colorMap = new Map(uniqueRates.map((rate, i) => [rate, colors[i % colors.length]]));

            return (
              <div className="mb-4">
                <div className="relative h-10 bg-gray-100 rounded overflow-hidden border border-gray-200">
                  {timelineRates.map((rate) => {
                    const start = parseDate(rate.validFrom);
                    // Add 1 day to validTo so the segment covers the full last day
                    const rawEnd = rate.validTo ? parseDate(rate.validTo) : endDate;
                    const end = rate.validTo ? new Date(rawEnd.getTime() + 86400000) : rawEnd;
                    const leftPct = ((start.getTime() - firstDate.getTime()) / totalMs) * 100;
                    const widthPct = ((end.getTime() - start.getTime()) / totalMs) * 100;
                    const color = colorMap.get(rate.hourlyCosts ?? 0) ?? 'bg-gray-400';
                    return (
                      <div
                        key={rate.id}
                        className={`absolute top-0 h-full ${color} flex items-center justify-center text-xs font-medium text-white border-r border-white/30 cursor-pointer hover:brightness-110 transition-all ${highlightedRateId === rate.id ? 'ring-2 ring-offset-1 ring-gray-900 z-20' : ''}`}
                        style={{ left: `${leftPct}%`, width: `${Math.max(widthPct, 0.5)}%` }}
                        title={`${formatDisplayDate(rate.validFrom)} → ${rate.validTo ? formatDisplayDate(rate.validTo) : 'ongoing'}: ${formatCurrency(rate.hourlyCosts ?? 0)}`}
                        onClick={() => setHighlightedRateId(highlightedRateId === rate.id ? null : rate.id)}
                      >
                        {widthPct > 8 && <span className="truncate px-1">{formatCurrency(rate.hourlyCosts ?? 0)}</span>}
                      </div>
                    );
                  })}
                  {/* Today marker */}
                  {(() => {
                    const todayPct = ((today.getTime() - firstDate.getTime()) / totalMs) * 100;
                    if (todayPct < 0 || todayPct > 100) return null;
                    return (
                      <div
                        className="absolute top-0 h-full w-0.5 bg-red-600 z-10"
                        style={{ left: `${todayPct}%` }}
                        title="Today"
                      />
                    );
                  })()}
                </div>
                {/* Date labels at rate boundaries */}
                <div className="relative h-5 mt-0.5">
                  {dateLabels.map((m, i) => (
                    <span
                      key={i}
                      className="absolute text-[10px] text-gray-400"
                      style={{ left: `${m.position}%` }}
                    >
                      {m.label}
                    </span>
                  ))}
                </div>
              </div>
            );
          })()}

          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valid From</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valid To</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Hourly Cost</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Part-Time</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredRates.map((rate) => (
                  <tr key={rate.id} className={`hover:bg-gray-50 transition-colors cursor-pointer ${highlightedRateId === rate.id ? 'bg-blue-50 ring-1 ring-inset ring-blue-300' : ''}`} onClick={() => setHighlightedRateId(highlightedRateId === rate.id ? null : rate.id)}>
                    <td className="px-4 py-3 text-sm text-gray-900">{formatDisplayDate(rate.validFrom)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatDisplayDate(rate.validTo)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                      {rate.hourlyCosts != null ? formatCurrency(rate.hourlyCosts) : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                      {((rate.partTime ?? 1) * 100).toFixed(0)}%
                    </td>
                    <td className="px-4 py-3 text-right">
                      {filteredRates.length > 1 && (
                        <button
                          className="text-xs text-red-500 hover:text-red-700 font-medium"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete rate ${formatCurrency(rate.hourlyCosts ?? 0)} (${formatDisplayDate(rate.validFrom)} → ${rate.validTo ? formatDisplayDate(rate.validTo) : 'ongoing'})?`)) {
                              deleteRateMutation.mutate(rate.id);
                            }
                          }}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      {/* Section: Project Roles (Skills) */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-3">Project Roles</h2>
        <p className="text-sm text-gray-500 mb-3">Roles this person can fill in capacity planning.</p>
        <div className="flex flex-wrap gap-2">
          {(allProjectRolesData?.data ?? []).map((role) => {
            const isTagged = (userRoleTagsData?.data ?? []).some((t) => t.role.id === role.id);
            return (
              <button
                key={role.id}
                type="button"
                onClick={() => {
                  if (!userId) return;
                  const currentIds = (userRoleTagsData?.data ?? []).map((t) => t.role.id);
                  const newIds = isTagged ? currentIds.filter((rid) => rid !== role.id) : [...currentIds, role.id];
                  setUserTagsMutation.mutate({ userId, roleIds: newIds });
                }}
                className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                  isTagged
                    ? 'bg-primary-50 text-primary-700 border-primary-300'
                    : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                }`}
              >
                {role.code ? `${role.code} — ${role.name}` : role.name}
                {isTagged && (
                  <svg className="ml-1.5 h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            );
          })}
          {(allProjectRolesData?.data ?? []).length === 0 && (
            <p className="text-sm text-gray-400">No project roles defined. Create them in Admin &gt; Project Roles.</p>
          )}
        </div>
      </div>

      {/* Section 3: Project Assignments */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">Project Assignments</h2>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-1.5 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={showInactiveProjects}
                onChange={(e) => setShowInactiveProjects(e.target.checked)}
                className="rounded border-gray-300"
              />
              Show inactive projects
            </label>
            <label className="flex items-center gap-1.5 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={showPastAssignments}
                onChange={(e) => setShowPastAssignments(e.target.checked)}
                className="rounded border-gray-300"
              />
              Show past
            </label>
          </div>
        </div>

        {loadingAssignments ? (
          <div className="flex justify-center py-4"><Spinner /></div>
        ) : filteredAssignments.length === 0 ? (
          <p className="text-sm text-gray-500">No project assignments.</p>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortableHeader label="Project" sortKey="project" current={assignmentSort} onSort={setAssignmentSort} />
                  <SortableHeader label="Start Date" sortKey="startDate" current={assignmentSort} onSort={setAssignmentSort} />
                  {showPastAssignments && (
                    <SortableHeader label="End Date" sortKey="endDate" current={assignmentSort} onSort={setAssignmentSort} />
                  )}
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Hourly Rate</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Investment</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Billing Rate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {groupedAssignments.map(({ assignment, isPast, isSubItem }) => (
                  editingAssignmentId === assignment.id ? (
                    <tr key={assignment.id} className="bg-gray-50">
                      <td colSpan={showPastAssignments ? 9 : 8} className="px-4 py-3">
                        <AssignmentForm
                          projectId={assignment.projectId}
                          assignment={{
                            id: assignment.id,
                            userId: assignment.userId,
                            projectId: assignment.projectId,
                            startDate: assignment.startDate,
                            endDate: assignment.endDate,
                            billingRate: assignment.billingRate,
                            hourlyCosts: assignment.hourlyCosts,
                            userInvestment: assignment.userInvestment,
                            roleName: assignment.roleName,
                            user: null,
                          }}
                          onSubmit={async (input) => {
                            // F22-003: wrap mutateAsync in try/catch so a rejection
                            // doesn't bubble up as an unhandledrejection. React Query
                            // surfaces the error via `updateAssignmentMutation.error`.
                            try {
                              await updateAssignmentMutation.mutateAsync({
                                projectId: assignment.projectId,
                                assignmentId: assignment.id,
                                input: input as UpdateAssignmentInput,
                              });
                            } catch {
                              // mutation error handled by React Query — leave form open
                            }
                          }}
                          onCancel={() => setEditingAssignmentId(null)}
                          isLoading={updateAssignmentMutation.isPending}
                        />
                      </td>
                    </tr>
                  ) : (
                  <tr key={assignment.id} className={isPast ? 'bg-gray-50 text-gray-400' : 'hover:bg-gray-50'}>
                    <td className="px-4 py-3 text-sm">
                      {isSubItem ? (
                        <span className="pl-5 text-gray-400 text-xs">
                          {assignment.startDate ? formatDisplayDate(assignment.startDate) : '?'} — {assignment.endDate ? formatDisplayDate(assignment.endDate) : '?'}
                        </span>
                      ) : assignment.project ? (
                        <Link
                          to={`/projects/${assignment.project.id}`}
                          className={isPast ? 'text-gray-400 hover:underline' : 'text-primary-600 hover:underline'}
                        >
                          {assignment.project.fullName || assignment.project.name || `Project #${assignment.projectId}`}
                        </Link>
                      ) : (
                        <span className="text-gray-400">Project #{assignment.projectId}</span>
                      )}
                    </td>
                    <td className={`px-4 py-3 text-sm ${isPast ? 'text-gray-400' : 'text-gray-900'}`}>
                      {formatDisplayDate(assignment.startDate)}
                    </td>
                    {showPastAssignments && (
                      <td className="px-4 py-3 text-sm text-gray-400">
                        {formatDisplayDate(assignment.endDate)}
                      </td>
                    )}
                    <td className={`px-4 py-3 text-sm text-right ${isPast ? 'text-gray-400' : 'text-gray-900'}`}>
                      {assignment.hourlyCosts != null ? formatCurrency(Number(assignment.hourlyCosts)) : '-'}
                    </td>
                    <td className={`px-4 py-3 text-sm text-right ${isPast ? 'text-gray-400' : 'text-gray-900'}`}>
                      {assignment.userInvestment != null ? formatCurrency(Number(assignment.userInvestment)) : '-'}
                    </td>
                    <td className={`px-4 py-3 text-sm text-right ${isPast ? 'text-gray-400' : 'text-gray-900'}`}>
                      {assignment.billingRate != null ? formatCurrency(Number(assignment.billingRate)) : '-'}
                    </td>
                    <td className={`px-4 py-3 text-sm ${isPast ? 'text-gray-400' : 'text-gray-600'}`}>
                      {assignment.roleName || '-'}
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      {!isPast && (
                        <>
                          <button
                            className="text-sm text-gray-600 hover:text-gray-900"
                            onClick={() => setEditingAssignmentId(assignment.id)}
                          >
                            Edit
                          </button>
                          <button
                            className="text-sm text-red-500 hover:text-red-700"
                            onClick={() => setDeleteAssignmentId(assignment.id)}
                          >
                            Remove
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                  )
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Rate Modal */}
      {rateModalOpen && (
        <RateModal
          currentRate={currentRate}
          activeAssignments={activeAssignmentsOnly}
          // FTE-02 cost-leg repoint (Phase 71 Plan 71-07 Task 3): hourlyCosts
          // pre-fill now reads FTE from the active employee-category
          // relationship (+ its EmployeeType.totalHoursPerMonth) instead of
          // the legacy partTime field + a hardcoded 167-hour average.
          employeeCategoryFte={activeEmployeeRelationship ? Number(activeEmployeeRelationship.fte) : null}
          employeeCategoryHoursPerMonth={activeEmployeeTypeHoursPerMonth}
          onClose={() => setRateModalOpen(false)}
          onSave={(data) => createRateMutation.mutate({ ...data, employeeTypeId: user.employeeTypeId ?? undefined })}
          isLoading={createRateMutation.isPending}
          error={createRateMutation.error}
        />
      )}

      {/* Remove Assignment Confirm */}
      <ConfirmModal
        isOpen={deleteAssignmentId !== null}
        onClose={() => setDeleteAssignmentId(null)}
        onConfirm={async () => {
          const a = assignments?.find((x) => x.id === deleteAssignmentId);
          if (!a) return;
          // F2-005: wrap mutateAsync in try/catch — previously an unawaited
          // rejection (network blip / 409) left the ConfirmModal stuck open
          // with no user feedback. React Query exposes the message via
          // `deleteAssignmentMutation.error` which is already surfaced
          // elsewhere on the page; we close the modal regardless so the
          // user isn't trapped.
          try {
            await deleteAssignmentMutation.mutateAsync({ projectId: a.projectId, assignmentId: a.id });
          } catch {
            // mutation error already exposed via React Query state
          } finally {
            setDeleteAssignmentId(null);
          }
        }}
        title="Remove Assignment"
        message="Are you sure you want to remove this assignment?"
        confirmText="Remove"
        variant="danger"
        isLoading={deleteAssignmentMutation.isPending}
      />

      {/* Archive Modal (UI-03) */}
      <ArchiveUserModal
        isOpen={showArchiveConfirm}
        onClose={() => setShowArchiveConfirm(false)}
        user={user ?? null}
        defaultDriveTransferTo={currentUser?.email ?? ''}
        onArchived={() => {
          queryClient.invalidateQueries({ queryKey: ['adminUser', userId] });
          queryClient.invalidateQueries({ queryKey: ['auditTrail', 'User', userId] });
        }}
      />

      {/* Restore Modal */}
      <Modal
        isOpen={showRestoreConfirm}
        onClose={() => setShowRestoreConfirm(false)}
        title="Restore User"
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowRestoreConfirm(false)} disabled={restoreMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={async () => {
                // F21-002: see archive-modal note above — same rationale here.
                try {
                  // FBV-01: pass the chosen tier to restoreMutation so the backend
                  // can persist it on the 'claude' cell and forward it as appAccessTier.
                  await restoreMutation.mutateAsync({ id: user!.id, claudeTier: restoreTier, skipOnboarding });
                  queryClient.invalidateQueries({ queryKey: ['adminUser', userId] });
                  queryClient.invalidateQueries({ queryKey: ['auditTrail', 'User', userId] });
                  queryClient.invalidateQueries({ queryKey: ['userWorkflowRuns', Number(userId)] });
                  setShowRestoreConfirm(false);
                } catch {
                  // mutation error handled by React Query — banner renders below
                }
              }}
              isLoading={restoreMutation.isPending}
            >
              Restore User
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            This will restore {user?.firstName} {user?.lastName} and make their account active again.
          </p>
          {restoreMutation.isError && (
            <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
              {(restoreMutation.error as Error | null)?.message || 'Failed to restore user. Please try again.'}
            </div>
          )}
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={skipOnboarding}
              onChange={(e) => setSkipOnboarding(e.target.checked)}
              className="rounded border-gray-300"
            />
            Skip onboarding workflow
          </label>
          {/* FBV-01: Claude tier selector — admin picks Standard or Premium for restore */}
          <label className="flex items-center gap-2 text-sm text-gray-700">
            Claude tier
            <select
              value={restoreTier}
              onChange={(e) => setRestoreTier(e.target.value as ClaudeTierChoice)}
              className="rounded-lg border border-gray-300 px-2 py-1 text-sm"
            >
              <option value="claude-standard">Claude: Standard</option>
              <option value="claude-premium">Claude: Premium</option>
                <option value="none">Bez Claude licence</option>
            </select>
          </label>
        </div>
      </Modal>

      {/* Onboarding: Self (skill checklist) + System (workflow runs) tabs */}
      <div className="mt-8">
        <OnboardingPanel userId={userId!} isArchived={!!user?.deletedAt} autoExpand={searchParams.get('section') === 'workflows'} />
      </div>

      {/* History */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">History</h3>
        <StateHistory history={auditTrail?.data ?? []} />
      </div>
    </div>
  );
}

type SortKey = 'project' | 'startDate' | 'endDate';
type SortState = { key: SortKey; dir: 'asc' | 'desc' };

function SortableHeader({ label, sortKey, current, onSort }: {
  label: string;
  sortKey: SortKey;
  current: SortState;
  onSort: (s: SortState) => void;
}) {
  const active = current.key === sortKey;
  return (
    <th
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700"
      onClick={() => onSort({ key: sortKey, dir: active && current.dir === 'asc' ? 'desc' : 'asc' })}
    >
      {label}
      {active && (
        <span className="ml-1">{current.dir === 'asc' ? '\u2191' : '\u2193'}</span>
      )}
    </th>
  );
}

function RateModal({
  currentRate,
  activeAssignments,
  employeeCategoryFte,
  employeeCategoryHoursPerMonth,
  onClose,
  onSave,
  isLoading,
  error,
}: {
  currentRate: UserRate | null;
  activeAssignments: UserAssignment[];
  // Phase 71 (FTE-02): the active employee-category relationship's FTE and
  // its EmployeeType.totalHoursPerMonth, or null when the user has no active
  // employee-category relationship (contractor-only / unassigned) — falls
  // back to 1.0 FTE / 167h below, same defaults the legacy formula used.
  employeeCategoryFte: number | null;
  employeeCategoryHoursPerMonth: number | null;
  onClose: () => void;
  onSave: (data: { validFrom: string; validTo?: string | null; hourlyCosts: number; salary?: number | null; partTime?: number; excludeAssignmentIds?: number[] }) => void;
  isLoading: boolean;
  error: Error | null;
}) {
  const EMPLOYER_COST_MULTIPLIER = 1.35;
  const THIRTEENTH_MONTH = 13 / 12;
  // FTE-02 cost-leg repoint (Phase 71 Plan 71-07 Task 3): the hourlyCosts
  // pre-fill formula now reads FTE + monthly-hours from the user's active
  // employee-category EmploymentRelationship (71-RESEARCH.md "FTE-Source
  // Reconciliation") instead of the legacy UserRate.partTime field + a
  // hardcoded 167-hour average. `partTime` itself is left untouched as
  // read-only historical metadata — still displayed in the Rates table
  // above and still persisted on the UserRate row via `formData.partTime`
  // below, just no longer consulted by this calculation. The 1.0 FTE /
  // 167h fallbacks preserve today's behavior for a user with no active
  // employee-category relationship (matching the legacy formula's own
  // `partTime ?? 1` / hardcoded-167 defaults) — the admin can always
  // override the computed value before saving.
  const HOURS_PER_MONTH_FALLBACK = 167;
  const effectiveFte = employeeCategoryFte ?? 1;
  const effectiveHoursPerMonth = employeeCategoryHoursPerMonth ?? HOURS_PER_MONTH_FALLBACK;

  const calculateHourlyCosts = (salary: number): number =>
    (salary * EMPLOYER_COST_MULTIPLIER * THIRTEENTH_MONTH) / (effectiveFte * effectiveHoursPerMonth);

  const [formData, setFormData] = useState(() => {
    const partTime = currentRate?.partTime ?? 1;
    const salary = currentRate?.salary ?? null;
    // Auto-calculate hourly costs from salary if salary exists
    const hourlyCosts = salary && salary > 0
      ? calculateHourlyCosts(salary).toFixed(2)
      : currentRate?.hourlyCosts?.toString() || '';
    return {
      validFrom: formatLocalDate(new Date()),
      validTo: '',
      hourlyCosts,
      salary: salary?.toString() || '',
      partTime: partTime.toString(),
    };
  });
  // All assignments checked (will be updated) by default
  const [excludedIds, setExcludedIds] = useState<Set<number>>(new Set());

  const handleSalaryChange = (value: string) => {
    const gross = parseFloat(value);
    if (gross > 0) {
      setFormData((prev) => ({ ...prev, salary: value, hourlyCosts: calculateHourlyCosts(gross).toFixed(2) }));
    } else {
      setFormData((prev) => ({ ...prev, salary: value }));
    }
  };

  const toggleAssignment = (id: number) => {
    setExcludedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validTo = formData.validTo || null;
    onSave({
      validFrom: formData.validFrom,
      validTo: validTo,
      hourlyCosts: parseFloat(formData.hourlyCosts),
      salary: formData.salary ? parseFloat(formData.salary) : null,
      partTime: parseFloat(formData.partTime) || 1,
      excludeAssignmentIds: excludedIds.size > 0 ? Array.from(excludedIds) : undefined,
    });
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Update Rate"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <p className="text-sm text-red-600">{(error as Error).message}</p>}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valid From</label>
            <DateField
              value={formData.validFrom}
              onChange={(nextDate) => setFormData({ ...formData, validFrom: nextDate })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valid To</label>
            <DateField
              value={formData.validTo}
              onChange={(nextDate) => setFormData({ ...formData, validTo: nextDate })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Salary (Kč/month)</label>
            <input
              type="number"
              step="1"
              min="0"
              value={formData.salary}
              onChange={(e) => handleSalaryChange(e.target.value)}
              placeholder="e.g. 50000"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Pre-fill uses FTE {effectiveFte}
              {employeeCategoryFte == null && ' (no active employee-category relationship — defaulting to 1.0)'}
              {' '}× {effectiveHoursPerMonth}h/month.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hourly Cost (Kč/hr)</label>
            <input
              type="number"
              step="0.01"
              value={formData.hourlyCosts}
              onChange={(e) => setFormData({ ...formData, hourlyCosts: e.target.value, salary: '' })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <p className="text-[11px] text-gray-500 mt-1">Computed from Salary — override freely.</p>
          </div>
        </div>

        {activeAssignments.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Update assignment rates
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Checked assignments will have their hourly rate updated (if lower than the new rate). Uncheck to keep the current rate.
            </p>
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-48 overflow-y-auto">
              {[...activeAssignments].sort((a, b) =>
                (a.project?.fullName || a.project?.name || '').localeCompare(b.project?.fullName || b.project?.name || '')
              ).map((a) => (
                <label key={a.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!excludedIds.has(a.id)}
                    onChange={() => toggleAssignment(a.id)}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-900 flex-1">
                    {a.project?.fullName || a.project?.name || `Project #${a.projectId}`}
                  </span>
                  <span className="text-xs text-gray-500">
                    {a.hourlyCosts != null ? formatCurrency(Number(a.hourlyCosts)) : '-'}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isLoading}>
            Update Rate
          </Button>
        </div>
      </form>
    </Modal>
  );
}
